/**
 * Raw-`fetch` client for the delegation-authenticated `/platform/agent` routes (FEAT-1698).
 *
 * Axios is deliberately not used: the turn endpoint returns `text/event-stream` and the panel needs
 * `response.body` as a `ReadableStream` plus `AbortController` cancellation. Every request carries
 * the `mlda_` delegation bearer and is retried exactly once against a freshly minted credential when
 * platform-api answers 401, which is how expiry mid-session is absorbed without user-visible errors.
 */
import { platformApiConfig } from 'src/config';

import { parseAgentErrorEnvelope, parseAgentThreadHistory } from './protocol';
import type { AgentErrorEnvelope, AgentStreamEvent, AgentThreadMessage } from './protocol';
import { readAgentSseStream } from './sseStream';

const AGENT_THREADS_PATH = '/platform/agent/threads';

/**
 * Origin the `/platform/agent` routes resolve against.
 *
 * The M4 plan says "`API_URL`-relative", which predates platform-api getting its own origin:
 * `API_URL` (`src/utils/api`) points at the Java backend, while `/platform/agent` is mounted by
 * `mountAgent` in platform-api. `platformApiConfig.baseUrl`
 * (`PUBLIC_MLOOP_PLATFORM_API_BASE_URL`) is therefore the correct configured base, and the only
 * one where these routes exist — using `API_URL` would 404 in every environment.
 */
export const AGENT_API_BASE_URL = platformApiConfig.baseUrl;

/** Supplies a delegation bearer, re-minting when `forceRemint` is set. */
export type AgentTokenProvider = (forceRemint?: boolean) => Promise<string>;

/** A failed `/platform/agent` request, carrying the platform-api error code when returned. */
export class AgentRequestError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'AgentRequestError';
  }
}

const agentUrl = (path: string): string => `${AGENT_API_BASE_URL}${path}`;

/** Issues an authorized request, transparently re-minting the delegation once on 401. */
async function agentFetch(path: string, init: RequestInit, getToken: AgentTokenProvider): Promise<Response> {
  const send = async (forceRemint: boolean): Promise<Response> => {
    const token = await getToken(forceRemint);
    return fetch(agentUrl(path), {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${token}` },
    });
  };

  const response = await send(false);
  if (response.status !== 401) {
    return response;
  }
  return await send(true);
}

/** Reads the platform-api error envelope, falling back to the HTTP status when it is absent. */
async function toRequestError(response: Response): Promise<AgentRequestError> {
  let envelope: AgentErrorEnvelope | null = null;
  try {
    envelope = parseAgentErrorEnvelope(await response.json());
  } catch {
    // Non-JSON error body (proxy or gateway failure); fall back to the status-derived envelope.
  }
  return new AgentRequestError(
    response.status,
    envelope?.code ?? 'INTERNAL',
    envelope?.message ?? `Agent request failed with status ${response.status}.`
  );
}

/** Fetches one thread's persisted messages; an unknown thread resolves to an empty history. */
export async function getAgentThreadHistory(
  threadId: string,
  getToken: AgentTokenProvider
): Promise<AgentThreadMessage[]> {
  const response = await agentFetch(
    `${AGENT_THREADS_PATH}/${encodeURIComponent(threadId)}`,
    { method: 'GET' },
    getToken
  );
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw await toRequestError(response);
  }
  return parseAgentThreadHistory(await response.json());
}

/**
 * Posts a user message and streams the resulting turn, invoking `onEvent` per decoded SSE event.
 *
 * Resolves when the server closes the stream. Aborting `signal` rejects with an `AbortError`, which
 * the caller treats as a user-cancelled turn rather than a failure.
 */
export async function streamAgentTurn(
  input: {
    threadId: string;
    message: string;
    signal: AbortSignal;
  },
  getToken: AgentTokenProvider,
  onEvent: (event: AgentStreamEvent) => void
): Promise<void> {
  const response = await agentFetch(
    `${AGENT_THREADS_PATH}/${encodeURIComponent(input.threadId)}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ message: input.message }),
      signal: input.signal,
    },
    getToken
  );

  if (!response.ok) {
    throw await toRequestError(response);
  }
  if (!response.body) {
    throw new AgentRequestError(response.status, 'INTERNAL', 'Agent stream returned no body.');
  }

  await readAgentSseStream(response.body, onEvent);
}
